import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "./PageLoader";
import axios from "axios";
import { toast } from "react-toastify";
import config from "./config";
import { getEcho } from "./echo";
import EmojiPicker from "./EmojiPicker";
import GifPicker from "./GifPicker";
import { ensureNotificationPermission } from "./chatNotify";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Avatar (image or coloured initial), with an optional online dot.
function Avatar({ name, img, primary, size = 40, ring = false, online = false }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      <span
        className={`flex h-full w-full items-center justify-center overflow-hidden rounded-full font-bold text-white ${
          ring ? "ring-2 ring-white shadow-sm" : ""
        }`}
        style={{ backgroundColor: primary, fontSize: size * 0.4 }}
      >
        {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : initial}
      </span>
      {online && (
        <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
      )}
    </span>
  );
}

// WhatsApp-style delivery ticks: single grey ✓ = sent, double grey ✓✓ =
// delivered, double blue ✓✓ = read.
function ReadTicks({ read, delivered }) {
  const color = read ? "#2563eb" : "#94a3b8";
  if (!delivered && !read) {
    return (
      <svg viewBox="0 0 12 12" className="h-3 w-3 shrink-0" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 6.5l3 3 6.2-7" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 18 12" className="h-3 w-[18px] shrink-0" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 6.5l3 3 6.2-7" />
      <path d="M6.5 6.5l3 3 6.2-7" />
    </svg>
  );
}

function darken(hex, amt = 0.18) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const ch = (h) => Math.max(0, Math.round(parseInt(h, 16) * (1 - amt)));
  return `rgb(${ch(m[1])}, ${ch(m[2])}, ${ch(m[3])})`;
}

function shortTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// "last seen 5m ago" style relative time.
function lastSeenText(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "last seen just now";
  if (sec < 3600) return `last seen ${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `last seen ${Math.floor(sec / 3600)}h ago`;
  const days = Math.floor(sec / 86400);
  if (days < 7) return `last seen ${days}d ago`;
  return `last seen ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
}

function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diff = Math.round((new Date(now.toDateString()) - new Date(d.toDateString())) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString([], {
    month: "long",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

function isImageUrl(s) {
  const v = (s || "").trim();
  return /^https?:\/\/\S+$/i.test(v) && (/\.(gif|png|jpe?g|webp)(\?\S*)?$/i.test(v) || /giphy\.com|tenor\.com/i.test(v));
}

// ---- inline UI icons (used instead of emoji for controls) ----------------
const StarIcon = ({ className, filled }) => (
  <svg className={className} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M12 3.5l2.6 5.27 5.82.85-4.21 4.1.99 5.79L12 16.77l-5.2 2.74.99-5.79-4.21-4.1 5.82-.85L12 3.5z" />
  </svg>
);
const BellIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1" />
  </svg>
);
const BellOffIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5.8A6 6 0 0118 11v3.2a2 2 0 00.6 1.4L20 17H8m-2.4 0H4l1.4-1.4A2 2 0 006 14.2V11M9 21a3 3 0 005.7-1M4 4l16 16" />
  </svg>
);
const BanIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
    <circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M5.6 5.6l12.8 12.8" />
  </svg>
);
const PinIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 17v4M8.5 4h7l-1 2.2v3l2 3.3v1.2H7.5V12.5l2-3.3v-3L8.5 4z" />
  </svg>
);
const ArchiveIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 01-2-2V5a1 1 0 011-1h16a1 1 0 011 1v1a2 2 0 01-2 2M5 8v10a1 1 0 001 1h12a1 1 0 001-1V8M10 12h4" />
  </svg>
);
const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5h6v2m-7 0l1 12h6l1-12" />
  </svg>
);
const UnreadDotIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5" /></svg>
);
const PhotoIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
    <rect x="4" y="5" width="16" height="14" rx="2" /><circle cx="9" cy="10" r="1.4" /><path strokeLinecap="round" strokeLinejoin="round" d="M5 17l4-4 3 3 3-3 4 4" />
  </svg>
);

export default function ChatModal({ token, member, primary = "#0274F7", onClose, onUnreadChange, startWith = null }) {
  const memberToken = member?.auth_token;
  const myId = member?.community_member_id;

  const [conversations, setConversations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [contactQuery, setContactQuery] = useState("");

  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null); // { id, body }
  const [replyTo, setReplyTo] = useState(null); // { id, is_me, preview }
  const [uploading, setUploading] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [onlineIds, setOnlineIds] = useState(() => new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [reactFor, setReactFor] = useState(null); // message id whose quick-react row is open
  const [menuDir, setMenuDir] = useState("down"); // "down" | "up" — chosen from caret's viewport position
  const [flash, setFlash] = useState(""); // brief compact confirmation pill (copy / favorite / mute / block)
  const flashTimer = useRef(null);
  const showFlash = (text) => {
    setFlash(text);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(""), 1600);
  };
  // Confirmations: a regular toast on large screens, the compact pill on mobile
  // (react-toastify goes full-width on phones, which is too heavy for these).
  const notify = (text) => {
    if (typeof window !== "undefined" && window.innerWidth < 640) showFlash(text);
    else toast.info(text);
  };

  // Open the message-options menu anchored to the tapped caret, deciding up vs
  // down from the live space below it so the menu always stays on screen.
  const openMsgMenu = (id, evt) => {
    if (reactFor === id) {
      setReactFor(null);
      return;
    }
    const rect = evt.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setMenuDir(spaceBelow < 260 ? "up" : "down");
    setReactFor(id);
  };
  const [communityId, setCommunityId] = useState(null);
  const [convoSearch, setConvoSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [viewArchived, setViewArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [viewBlocked, setViewBlocked] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [rowMenu, setRowMenu] = useState(null); // conversation_id with its row menu open
  const searchTimer = useRef(null);

  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = active?.conversation_id || null;
  const initedRef = useRef(false);
  const lastWhisperRef = useRef(0);
  const typingTimer = useRef(null);
  // Whether the view is pinned to the bottom (so refreshes auto-scroll only
  // when the user is already at the latest message, not while reading history).
  const stickRef = useRef(true);

  const sentBubble = { background: `linear-gradient(135deg, ${primary} 0%, ${darken(primary, 0.2)} 100%)` };
  const myBubble = { backgroundColor: `${primary}1f`, color: "#1e293b" };

  const api = (path, body) =>
    axios.post(`${config.COMMUNITY_API_URL}/${path}`, { community_token: token, member_token: memberToken, ...body });

  // ---- data loaders -----------------------------------------------------
  const loadConversations = useCallback(async () => {
    try {
      const res = await api("community-chat-conversations", viewArchived ? { archived: 1 } : {});
      if (res.data?.error === 0) {
        setConversations(res.data.conversations || []);
        if (res.data.community_id) setCommunityId(res.data.community_id);
        setArchivedCount(res.data.archived_count || 0);
        onUnreadChange?.(res.data.total_unread || 0);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, memberToken, viewArchived]);

  const loadFavorites = useCallback(async () => {
    try {
      const res = await api("community-chat-favorites", {});
      if (res.data?.error === 0) setFavorites(res.data.favorites || []);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, memberToken]);

  const loadBlocked = useCallback(async () => {
    try {
      const res = await api("community-chat-blocked", {});
      if (res.data?.error === 0) setBlocked(res.data.blocked || []);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, memberToken]);

  const unblockMember = async (mtoken) => {
    try {
      const res = await api("community-chat-block", { target_member_token: mtoken, action: "unblock" });
      if (res.data?.error === 0) {
        notify("Member unblocked");
        loadBlocked();
        loadConversations();
      }
    } catch {
      /* ignore */
    }
  };

  const onSearchChange = (v) => {
    setConvoSearch(v);
    clearTimeout(searchTimer.current);
    if (v.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api("community-chat-search", { q: v.trim() });
        if (res.data?.error === 0) setSearchResults(res.data.results || []);
      } catch {
        /* ignore */
      }
    }, 300);
  };

  const convoAction = async (cid, action) => {
    setRowMenu(null);
    try {
      await api("community-chat-conversation-action", { conversation_id: cid, action });
      if (action === "delete" && active?.conversation_id === cid) setActive(null);
      loadConversations();
    } catch {
      /* ignore */
    }
  };

  const loadContacts = useCallback(
    async (q = "") => {
      try {
        const res = await api("community-chat-contacts", { q });
        if (res.data?.error === 0) setContacts(res.data.contacts || []);
      } catch {
        /* ignore */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, memberToken]
  );

  const openConversation = useCallback(
    async (convo) => {
      setActive(convo);
      setShowContacts(false);
      setEditing(null);
      setMenuOpen(false);
      setLoadingMsgs(true);
      setMessages([]);
      stickRef.current = true; // pin to bottom when opening a thread
      try {
        const res = await api("community-chat-messages", { conversation_id: convo.conversation_id });
        if (res.data?.error === 0) {
          setMessages(res.data.messages || []);
          setHasMore(!!res.data.has_more);
        }
        setConversations((list) => {
          const next = list.map((c) => (c.conversation_id === convo.conversation_id ? { ...c, unread: 0 } : c));
          onUnreadChange?.(next.reduce((s, c) => s + (c.unread || 0), 0));
          return next;
        });
      } catch {
        toast.error("Could not load messages.");
      } finally {
        setLoadingMsgs(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, memberToken]
  );

  const loadOlder = async () => {
    if (!active || loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const res = await api("community-chat-messages", {
        conversation_id: active.conversation_id,
        before_id: messages[0].id,
      });
      if (res.data?.error === 0) {
        const older = res.data.messages || [];
        setMessages((m) => [...older, ...m]);
        setHasMore(!!res.data.has_more);
        // Preserve scroll position after prepending.
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight;
        });
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingMore(false);
    }
  };

  const startChatWith = async (contact) => {
    try {
      const res = await api("community-chat-open", { target_member_token: contact.member_token });
      if (res.data?.error === 0) {
        const convo = res.data.conversation;
        setConversations((list) =>
          list.some((c) => c.conversation_id === convo.conversation_id)
            ? list
            : [{ ...convo, last_message: null, last_message_at: null, unread: 0 }, ...list]
        );
        openConversation(convo);
      } else {
        toast.error(res.data?.msg || "Could not start chat.");
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not start chat.");
    }
  };

  // ---- sending / editing ------------------------------------------------
  const postMessage = async (body) => {
    if (!body || !active || sending) return false;
    stickRef.current = true; // my own send → stay pinned to bottom
    setSending(true);
    const replyId = replyTo?.id || 0;
    try {
      const res = await api("community-chat-send", { conversation_id: active.conversation_id, body, reply_to_id: replyId });
      if (res.data?.error === 0) {
        const msg = res.data.message;
        setReplyTo(null);
        setMessages((m) => (m.some((x) => x.id === msg.id) ? m : [...m, msg]));
        setConversations((list) => {
          const exists = list.find((c) => c.conversation_id === active.conversation_id);
          const updated = { ...(exists || active), last_message: body, last_message_at: msg.created_at, unread: 0 };
          return [updated, ...list.filter((c) => c.conversation_id !== active.conversation_id)];
        });
        return true;
      }
      toast.error(res.data?.msg || "Could not send.");
      return false;
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not send.");
      return false;
    } finally {
      setSending(false);
    }
  };

  const saveEdit = async () => {
    const body = text.trim();
    if (!body || !editing) return;
    const { id } = editing;
    try {
      const res = await api("community-chat-edit", { conversation_id: active.conversation_id, message_id: id, body });
      if (res.data?.error === 0) {
        setMessages((m) => m.map((x) => (x.id === id ? { ...x, body, edited: true } : x)));
        setEditing(null);
        setText("");
      } else {
        toast.error(res.data?.msg || "Could not edit.");
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not edit.");
    }
  };

  const send = async () => {
    if (editing) return saveEdit();
    const body = text.trim();
    if (!body) return;
    setText("");
    const ok = await postMessage(body);
    if (!ok) setText(body);
  };

  const sendGif = (url) => {
    if (url) postMessage(url);
  };

  const uploadImage = async (file) => {
    if (!file || !active) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("community_token", token);
      fd.append("member_token", memberToken);
      fd.append("image", file);
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-chat-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.error === 0 && res.data.url) {
        await postMessage(res.data.url);
      } else {
        toast.error(res.data?.msg || "Upload failed.");
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const deleteMsg = async (id) => {
    try {
      const res = await api("community-chat-delete", { conversation_id: active.conversation_id, message_id: id });
      if (res.data?.error === 0) {
        setMessages((m) => m.map((x) => (x.id === id ? { ...x, deleted: true, body: null, reactions: [] } : x)));
      }
    } catch {
      toast.error("Could not delete.");
    }
  };

  const toggleReaction = async (msg, emoji) => {
    setReactFor(null);
    try {
      const res = await api("community-chat-react", {
        conversation_id: active.conversation_id,
        message_id: msg.id,
        emoji,
      });
      if (res.data?.error === 0) {
        setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, reactions: res.data.reactions || [] } : x)));
      }
    } catch {
      /* ignore */
    }
  };

  // ---- thread-level menu actions ---------------------------------------
  const toggleMute = async () => {
    if (!active) return;
    const cur = conversations.find((c) => c.conversation_id === active.conversation_id);
    const action = cur?.muted ? "unmute" : "mute";
    try {
      const res = await api("community-chat-mute", { conversation_id: active.conversation_id, action });
      if (res.data?.error === 0) {
        setConversations((list) =>
          list.map((c) => (c.conversation_id === active.conversation_id ? { ...c, muted: res.data.muted ? 1 : 0 } : c))
        );
        notify(res.data.muted ? "Conversation muted" : "Conversation unmuted");
      }
    } catch {
      /* ignore */
    }
    setMenuOpen(false);
  };

  const toggleFavorite = async () => {
    if (!active?.member_token) return;
    const isFav = favorites.some((f) => f.member_token === active.member_token);
    setMenuOpen(false);
    try {
      const res = await api("community-chat-favorite", {
        target_member_token: active.member_token,
        action: isFav ? "remove" : "add",
      });
      if (res.data?.error === 0) {
        loadFavorites();
        notify(res.data.favorite ? "Added to favorites" : "Removed from favorites");
      }
    } catch {
      /* ignore */
    }
  };

  const blockMember = async () => {
    if (!active?.member_token) return;
    setMenuOpen(false);
    try {
      const res = await api("community-chat-block", { target_member_token: active.member_token, action: "block" });
      if (res.data?.error === 0) {
        notify(`${active.name} blocked`);
        setActive(null);
        loadConversations();
        loadBlocked();
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not block.");
    }
  };

  // ---- initial load + poll ---------------------------------------------
  useEffect(() => {
    ensureNotificationPermission();
    loadConversations();
    loadFavorites();
    loadBlocked();
    const t = setInterval(loadConversations, 15000);
    return () => clearInterval(t);
  }, [loadConversations, loadFavorites, loadBlocked]);

  useEffect(() => {
    if (initedRef.current || !startWith) return;
    initedRef.current = true;
    startChatWith({ member_token: startWith });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startWith]);

  // ---- real-time for the active thread ---------------------------------
  useEffect(() => {
    if (!active?.conversation_id) return undefined;
    const echo = getEcho({ communityToken: token, memberToken });
    if (!echo) return undefined;
    const channel = `conversation.${active.conversation_id}`;
    const ch = echo.private(channel);

    ch.listen(".message.sent", (payload) => {
      const msg = payload?.message;
      if (!msg || activeIdRef.current !== payload.conversation_id) return;
      setMessages((m) => (m.some((x) => x.id === msg.id) ? m : [...m, { ...msg, is_me: msg.sender_id === myId }]));
      if (msg.sender_id !== myId) {
        setOtherTyping(false);
        api("community-chat-read", { conversation_id: payload.conversation_id }).catch(() => {});
        setConversations((list) =>
          list.map((c) =>
            c.conversation_id === payload.conversation_id
              ? { ...c, last_message: msg.body, last_message_at: msg.created_at, unread: 0 }
              : c
          )
        );
      }
    });

    ch.listen(".thread.event", (evt) => {
      if (!evt || activeIdRef.current !== evt.conversation_id) return;
      const d = evt.data || {};
      if (evt.kind === "read") {
        if (d.reader_id !== myId) setMessages((m) => m.map((x) => (x.is_me ? { ...x, read: true, delivered: true } : x)));
      } else if (evt.kind === "delivered") {
        if (d.by !== myId) setMessages((m) => m.map((x) => (x.is_me ? { ...x, delivered: true } : x)));
      } else if (evt.kind === "edit") {
        setMessages((m) => m.map((x) => (x.id === d.id ? { ...x, body: d.body, edited: true } : x)));
      } else if (evt.kind === "delete") {
        setMessages((m) => m.map((x) => (x.id === d.id ? { ...x, deleted: true, body: null, reactions: [] } : x)));
      } else if (evt.kind === "react") {
        setMessages((m) =>
          m.map((x) => {
            if (x.id !== d.id) return x;
            const mineMap = {};
            (x.reactions || []).forEach((r) => (mineMap[r.emoji] = r.mine));
            return { ...x, reactions: (d.reactions || []).map((r) => ({ ...r, mine: !!mineMap[r.emoji] })) };
          })
        );
      }
    });

    ch.listenForWhisper("typing", (e) => {
      if (e?.id === myId) return;
      setOtherTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setOtherTyping(false), 3500);
    });

    return () => {
      try {
        echo.leave(channel);
      } catch (e) {
        /* ignore */
      }
      clearTimeout(typingTimer.current);
      setOtherTyping(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.conversation_id, token, memberToken, myId]);

  // ---- presence (online members in the community) ----------------------
  useEffect(() => {
    if (!communityId) return undefined;
    const echo = getEcho({ communityToken: token, memberToken });
    if (!echo) return undefined;
    const name = `community.${communityId}`;
    const apply = (ids) => setOnlineIds(new Set(ids.map((u) => Number(u.id ?? u))));
    try {
      echo
        .join(name)
        .here((users) => apply(users))
        .joining((u) => setOnlineIds((s) => new Set(s).add(Number(u.id))))
        .leaving((u) =>
          setOnlineIds((s) => {
            const n = new Set(s);
            n.delete(Number(u.id));
            return n;
          })
        );
    } catch (e) {
      /* ignore */
    }
    return () => {
      try {
        echo.leave(name);
      } catch (e) {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId, token, memberToken]);

  // Whisper "typing" (throttled) as the user types.
  const onType = (e) => {
    setText(e.target.value);
    if (!active || editing) return;
    const now = Date.now();
    if (now - lastWhisperRef.current > 1500) {
      lastWhisperRef.current = now;
      try {
        getEcho({ communityToken: token, memberToken })
          ?.private(`conversation.${active.conversation_id}`)
          .whisper("typing", { id: myId });
      } catch (err) {
        /* ignore */
      }
    }
  };

  // Auto-scroll to the newest message, but only when the user is pinned to the
  // bottom (so a background refresh doesn't yank them away from history).
  useEffect(() => {
    if (scrollRef.current && !loadingMore && stickRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, otherTyping, loadingMore]);

  const onScrollMessages = (e) => {
    const el = e.currentTarget;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    if (el.scrollTop < 40) loadOlder();
  };

  // Fallback poll for the OPEN thread: refresh tick states (delivered/read),
  // edits/deletes/reactions and any new messages even when Reverb isn't
  // connected. Merges by id so scroll/history aren't disturbed.
  useEffect(() => {
    if (!active?.conversation_id) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await api("community-chat-messages", { conversation_id: active.conversation_id });
        if (cancelled || res.data?.error !== 0) return;
        const fresh = res.data.messages || [];
        setMessages((prev) => {
          const map = new Map(prev.map((m) => [m.id, m]));
          let changed = false;
          fresh.forEach((fm) => {
            const ex = map.get(fm.id);
            if (!ex) {
              map.set(fm.id, fm);
              changed = true;
            } else if (
              ex.read !== fm.read ||
              ex.delivered !== fm.delivered ||
              ex.edited !== fm.edited ||
              ex.deleted !== fm.deleted ||
              ex.body !== fm.body ||
              JSON.stringify(ex.reactions) !== JSON.stringify(fm.reactions)
            ) {
              map.set(fm.id, { ...ex, ...fm });
              changed = true;
            }
          });
          if (!changed) return prev;
          return Array.from(map.values()).sort((a, b) => a.id - b.id);
        });
      } catch {
        /* ignore */
      }
    };
    const t = setInterval(refresh, 6000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.conversation_id, token, memberToken]);

  const openContacts = () => {
    setShowContacts(true);
    setActive(null);
    loadContacts("");
  };

  const startEdit = (m) => {
    setEditing({ id: m.id, body: m.body });
    setReplyTo(null);
    setText(m.body || "");
    setReactFor(null);
  };

  const startReply = (m) => {
    setEditing(null);
    setReplyTo({ id: m.id, is_me: m.is_me, preview: isImageUrl(m.body) ? "Photo" : m.body || "" });
    setReactFor(null);
  };

  const scrollToMessage = (id) => {
    const el = document.getElementById(`chatmsg-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const openSearchResult = (r) => {
    setConvoSearch("");
    setSearchResults(null);
    openConversation({
      conversation_id: r.conversation_id,
      name: r.name,
      img: r.img,
      member_token: r.member_token,
      other_id: r.other_id,
    });
    setTimeout(() => scrollToMessage(r.message_id), 350);
  };

  const copyMsg = (m) => {
    try {
      navigator.clipboard?.writeText(m.body || "");
      showFlash("Copied");
    } catch {
      /* ignore */
    }
    setReactFor(null);
  };

  const showListOnMobile = !active;
  const activeConvo = active ? conversations.find((c) => c.conversation_id === active.conversation_id) : null;
  const isMuted = !!activeConvo?.muted;
  const isFavorite = active ? favorites.some((f) => f.member_token === active.member_token) : false;
  const blockedSet = new Set(blocked.map((b) => b.member_token));
  const isActiveBlocked = active ? blockedSet.has(active.member_token) : false;
  const otherId = active?.other_id ?? activeConvo?.other_id;
  const otherOnline = otherId ? onlineIds.has(Number(otherId)) : false;

  const chatGlyph = "M8 10.5h8M8 14h5m-9 6.2l3.6-2.1H18a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v14.2z";

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-stretch justify-center bg-slate-900/50 backdrop-blur-sm sm:items-center sm:p-4">
      {/* Compact, centered "Copied" confirmation — stays small and on-screen on
          every device (react-toastify goes full-width on mobile). */}
      {flash && (
        <div className="pointer-events-none fixed inset-x-0 bottom-24 z-[70] flex justify-center px-4">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900/90 px-3.5 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            {flash}
          </span>
        </div>
      )}
      <div className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl ring-1 ring-black/5 sm:h-[80vh] sm:max-h-[700px] sm:w-[920px] sm:rounded-[22px] sm:flex-row">
        {/* ---------- Left: conversations / contacts ---------- */}
        <aside className={`${showListOnMobile ? "flex" : "hidden"} w-full flex-col border-r border-slate-100 bg-white sm:flex sm:w-[336px]`}>
          <div className="flex items-center justify-between px-4 pt-4 pb-3">
            <div>
              <h3 className="text-lg font-extrabold tracking-tight text-slate-900">{showContacts ? "New message" : "Messages"}</h3>
              <p className="text-[11px] font-medium text-slate-400">
                {showContacts
                  ? "Pick a member to chat with"
                  : `${conversations.length} ${conversations.length === 1 ? "conversation" : "conversations"}`}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={showContacts ? () => setShowContacts(false) : openContacts}
                aria-label={showContacts ? "Back" : "New message"}
                className="flex h-9 w-9 items-center justify-center rounded-full transition hover:brightness-95"
                style={{ backgroundColor: `${primary}14`, color: primary }}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                  <path strokeLinecap="round" strokeLinejoin="round" d={showContacts ? "M15 19l-7-7 7-7" : "M12 5v14m7-7H5"} />
                </svg>
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 sm:hidden"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Search messages */}
          {!showContacts && (
            <div className="px-3 pb-2 pt-1">
              <div className="relative">
                <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
                <input
                  value={convoSearch}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search messages…"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-8 text-sm text-slate-700 transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:outline-none focus:ring-0"
                />
                {convoSearch && (
                  <button type="button" onClick={() => onSearchChange("")} aria-label="Clear" className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Favorites quick-access strip */}
          {!showContacts && !viewArchived && !viewBlocked && searchResults === null && favorites.length > 0 && (
            <div className="border-b border-slate-100 px-3 pb-3 pt-1">
              <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Favorites</p>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {favorites.filter((f) => !blockedSet.has(f.member_token)).map((f) => (
                  <button
                    key={f.member_token}
                    type="button"
                    onClick={() => startChatWith(f)}
                    className="flex w-14 shrink-0 flex-col items-center gap-1"
                    title={`Chat with ${f.name}`}
                  >
                    <Avatar name={f.name} img={f.img} primary={primary} size={48} ring online={onlineIds.has(Number(f.other_id))} />
                    <span className="w-full truncate text-center text-[10px] font-medium text-slate-500">{f.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Section toggles: Archived / Blocked headers + entries */}
          {!showContacts && searchResults === null && (
            viewArchived ? (
              <button type="button" onClick={() => setViewArchived(false)} className="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                Archived
              </button>
            ) : viewBlocked ? (
              <button type="button" onClick={() => setViewBlocked(false)} className="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                Blocked members
              </button>
            ) : (
              <>
                {archivedCount > 0 && (
                  <button type="button" onClick={() => setViewArchived(true)} className="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-xs font-semibold text-slate-500 hover:bg-slate-50">
                    <ArchiveIcon className="h-4 w-4 text-slate-400" />
                    Archived <span className="text-slate-400">({archivedCount})</span>
                  </button>
                )}
                {blocked.length > 0 && (
                  <button type="button" onClick={() => { setViewBlocked(true); loadBlocked(); }} className="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-xs font-semibold text-slate-500 hover:bg-slate-50">
                    <BanIcon className="h-4 w-4 text-slate-400" />
                    Blocked <span className="text-slate-400">({blocked.length})</span>
                  </button>
                )}
              </>
            )
          )}

          <div className="flex-1 overflow-y-auto">
            {showContacts ? (
              <div>
                <div className="px-3 pb-2">
                  <div className="relative">
                    <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3M11 19a8 8 0 100-16 8 8 0 000 16z" />
                    </svg>
                    <input
                      value={contactQuery}
                      onChange={(e) => {
                        setContactQuery(e.target.value);
                        loadContacts(e.target.value);
                      }}
                      placeholder="Search members…"
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm text-slate-700 transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:outline-none focus:ring-0"
                    />
                  </div>
                </div>
                {contacts.length === 0 ? (
                  <p className="px-4 py-10 text-center text-sm text-slate-400">No members found.</p>
                ) : (
                  <ul className="px-2 py-1 space-y-0.5">
                    {contacts.filter((c) => !blockedSet.has(c.member_token)).map((c) => (
                      <li key={c.member_token}>
                        <button
                          type="button"
                          onClick={() => startChatWith(c)}
                          className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition hover:bg-slate-50"
                        >
                          <Avatar name={c.name} img={c.img} primary={primary} size={40} ring />
                          <span className="truncate text-sm font-semibold text-slate-700">{c.name}</span>
                          <svg className="ml-auto h-4 w-4 shrink-0 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : viewBlocked ? (
              blocked.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-slate-400">No blocked members.</p>
              ) : (
                <ul className="px-2 py-1 space-y-0.5">
                  {blocked.map((b) => (
                    <li key={b.member_token} className="flex items-center gap-3 rounded-xl px-2.5 py-2">
                      <Avatar name={b.name} img={b.img} primary={primary} size={42} ring />
                      <span className="flex-1 truncate text-sm font-semibold text-slate-700">{b.name}</span>
                      <button
                        type="button"
                        onClick={() => unblockMember(b.member_token)}
                        className="shrink-0 rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-600 transition hover:bg-slate-50"
                      >
                        Unblock
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : searchResults !== null ? (
              searchResults.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-slate-400">No messages found.</p>
              ) : (
                <ul className="px-2 py-1 space-y-0.5">
                  {searchResults.filter((r) => !blockedSet.has(r.member_token)).map((r) => (
                    <li key={`${r.conversation_id}-${r.message_id}`}>
                      <button type="button" onClick={() => openSearchResult(r)} className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition hover:bg-slate-50">
                        <Avatar name={r.name} img={r.img} primary={primary} size={40} ring />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center justify-between gap-2">
                            <span className="truncate text-sm font-semibold text-slate-800">{r.name}</span>
                            <span className="shrink-0 text-[11px] text-slate-400">{shortTime(r.created_at)}</span>
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-slate-500">{r.snippet}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl" style={{ backgroundColor: `${primary}12`, color: primary }}>
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d={chatGlyph} />
                  </svg>
                </span>
                <p className="text-sm font-semibold text-slate-600">{viewArchived ? "No archived chats" : "No conversations yet"}</p>
                {!viewArchived && (
                  <button type="button" onClick={openContacts} className="rounded-full px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:brightness-110" style={sentBubble}>
                    Start a chat
                  </button>
                )}
              </div>
            ) : (
              <div>
                {!viewArchived && favorites.length > 0 && (
                  <p className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-slate-400">Recent</p>
                )}
                <ul className="px-2 pb-2 space-y-0.5">
                {conversations.filter((c) => !blockedSet.has(c.member_token)).map((c) => {
                  const isActive = active?.conversation_id === c.conversation_id;
                  return (
                    <li key={c.conversation_id} className="relative">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => openConversation(c)}
                        className={`group relative flex w-full cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition ${isActive ? "" : "hover:bg-slate-50"}`}
                        style={isActive ? { backgroundColor: `${primary}12` } : undefined}
                      >
                        {isActive && <span className="absolute left-0 top-1/2 h-7 w-1 -translate-y-1/2 rounded-r-full" style={{ backgroundColor: primary }} />}
                        <Avatar name={c.name} img={c.img} primary={primary} size={46} ring online={onlineIds.has(Number(c.other_id))} />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center justify-between gap-2">
                            <span className="flex min-w-0 items-center gap-1">
                              {c.pinned ? <PinIcon className="h-3 w-3 shrink-0 text-slate-400" /> : null}
                              <span className={`truncate text-sm ${c.unread ? "font-extrabold text-slate-900" : "font-semibold text-slate-800"}`}>{c.name}</span>
                            </span>
                            <span className="shrink-0 text-[11px] font-medium text-slate-400">{shortTime(c.last_message_at)}</span>
                          </span>
                          <span className="mt-0.5 flex items-center justify-between gap-2">
                            <span className={`flex min-w-0 items-center gap-1 truncate text-xs ${c.unread ? "font-semibold text-slate-700" : "text-slate-400"}`}>
                              {c.muted ? <BellOffIcon className="h-3 w-3 shrink-0 text-slate-400" /> : null}
                              {c.last_message ? (
                                isImageUrl(c.last_message) ? (
                                  <span className="inline-flex items-center gap-1"><PhotoIcon className="h-3.5 w-3.5" /> Photo</span>
                                ) : (
                                  <span className="truncate">{c.last_message}</span>
                                )
                              ) : (
                                "No messages yet"
                              )}
                            </span>
                            {c.unread > 0 && (
                              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold text-white shadow-sm" style={sentBubble}>
                                {c.unread > 9 ? "9+" : c.unread}
                              </span>
                            )}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setRowMenu(rowMenu === c.conversation_id ? null : c.conversation_id); }}
                          aria-label="Conversation options"
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 opacity-100 transition hover:bg-slate-200 hover:text-slate-600 sm:opacity-0 sm:group-hover:opacity-100"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
                        </button>
                      </div>
                      {rowMenu === c.conversation_id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setRowMenu(null)} />
                          <div className="absolute right-3 top-12 z-20 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
                            <button type="button" onClick={() => convoAction(c.conversation_id, c.pinned ? "unpin" : "pin")} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                              <PinIcon className="h-4 w-4 text-slate-400" /> {c.pinned ? "Unpin" : "Pin to top"}
                            </button>
                            <button type="button" onClick={() => convoAction(c.conversation_id, c.archived ? "unarchive" : "archive")} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                              <ArchiveIcon className="h-4 w-4 text-slate-400" /> {c.archived ? "Unarchive" : "Archive"}
                            </button>
                            {!c.unread && (
                              <button type="button" onClick={() => convoAction(c.conversation_id, "mark-unread")} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                                <UnreadDotIcon className="h-3.5 w-3.5" style={{ color: primary }} /> Mark as unread
                              </button>
                            )}
                            <button type="button" onClick={() => convoAction(c.conversation_id, "delete")} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                              <TrashIcon className="h-4 w-4" /> Delete chat
                            </button>
                          </div>
                        </>
                      )}
                    </li>
                  );
                })}
                </ul>
              </div>
            )}
          </div>
        </aside>

        {/* ---------- Right: active thread ---------- */}
        <section className={`${showListOnMobile ? "hidden" : "flex"} min-w-0 flex-1 flex-col bg-white sm:flex`}>
          {active ? (
            <>
              <div className="relative flex items-center gap-3 border-b border-slate-100 px-3 py-3 sm:px-4">
                <button type="button" onClick={() => setActive(null)} aria-label="Back" className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 sm:hidden">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <Avatar name={active.name} img={active.img} primary={primary} size={42} ring online={otherOnline} />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-[15px] font-bold text-slate-900">
                    <span className="truncate">{active.name}</span>
                    {isFavorite && <StarIcon className="h-4 w-4 shrink-0 text-amber-400" filled title="Favorite" />}
                    {isMuted && <BellOffIcon className="h-4 w-4 shrink-0 text-slate-400" title="Muted" />}
                    {isActiveBlocked && <BanIcon className="h-4 w-4 shrink-0 text-rose-500" title="Blocked" />}
                  </p>
                  <p className="text-[11px] font-medium text-slate-400">
                    {otherTyping ? (
                      <span style={{ color: primary }}>typing…</span>
                    ) : otherOnline ? (
                      <span className="inline-flex items-center gap-1 text-emerald-500">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active now
                      </span>
                    ) : (active?.last_seen || activeConvo?.last_seen) ? (
                      lastSeenText(active?.last_seen || activeConvo?.last_seen)
                    ) : (
                      "Direct message"
                    )}
                  </p>
                </div>
                <button type="button" onClick={() => setMenuOpen((o) => !o)} aria-label="Options" className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="1.7" />
                    <circle cx="12" cy="12" r="1.7" />
                    <circle cx="12" cy="19" r="1.7" />
                  </svg>
                </button>
                <button type="button" onClick={onClose} aria-label="Close" className="hidden h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 sm:flex">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                    <div className="absolute right-3 top-14 z-20 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
                      <button type="button" onClick={toggleFavorite} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                        <StarIcon className={`h-4 w-4 ${isFavorite ? "text-amber-400" : "text-slate-400"}`} filled={isFavorite} /> {isFavorite ? "Remove favorite" : "Add to favorites"}
                      </button>
                      <button type="button" onClick={toggleMute} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                        {isMuted ? <BellIcon className="h-4 w-4 text-slate-400" /> : <BellOffIcon className="h-4 w-4 text-slate-400" />} {isMuted ? "Unmute" : "Mute"}
                      </button>
                      <button type="button" onClick={blockMember} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                        <BanIcon className="h-4 w-4" /> Block
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div ref={scrollRef} onScroll={onScrollMessages} className="flex-1 overflow-y-auto bg-gradient-to-b from-slate-50 to-white px-4 py-5">
                {loadingMsgs ? (
                  <div className="space-y-3">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className={`flex ${i % 2 ? "justify-end" : "justify-start"}`}>
                        <div className={`h-10 animate-pulse rounded-2xl bg-slate-200/70 ${i % 2 ? "w-40" : "w-52"}`} />
                      </div>
                    ))}
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: `${primary}12`, color: primary }}>
                      <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d={chatGlyph} />
                      </svg>
                    </span>
                    <p className="text-sm font-semibold text-slate-600">Say hi to {active.name}</p>
                    <p className="text-xs text-slate-400">This is the start of your conversation.</p>
                  </div>
                ) : (
                  <div>
                    {loadingMore && <p className="py-1 text-center text-[11px] text-slate-400">Loading earlier messages…</p>}
                    {(() => {
                      let lastDay = null;
                      return messages.map((m, idx) => {
                        const dl = dayLabel(m.created_at);
                        const showSep = dl && dl !== lastDay;
                        lastDay = dl;
                        const prev = messages[idx - 1];
                        const next = messages[idx + 1];
                        const firstOfGroup = !prev || prev.is_me !== m.is_me || dayLabel(prev.created_at) !== dl;
                        const lastOfGroup = !next || next.is_me !== m.is_me || dayLabel(next.created_at) !== dl;
                        // Options caret — sits BESIDE the bubble (not over the
                        // text) so it never covers the message. Always visible on
                        // mobile, hover-reveal on desktop.
                        const caretBtn = !m.deleted && (
                          <button
                            type="button"
                            onClick={(e) => openMsgMenu(m.id, e)}
                            aria-label="Message options"
                            className="flex h-7 w-7 shrink-0 self-center items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-black/5 transition hover:text-slate-600 sm:opacity-0 sm:group-hover:opacity-100"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" /></svg>
                          </button>
                        );
                        return (
                          <Fragment key={m.id}>
                            {showSep && (
                              <div className="my-3 flex justify-center">
                                <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-slate-400 shadow-sm ring-1 ring-black/5">{dl}</span>
                              </div>
                            )}
                            <div id={`chatmsg-${m.id}`} className={`group flex items-end gap-2 ${m.is_me ? "flex-row-reverse" : ""} ${showSep ? "" : firstOfGroup ? "mt-4" : "mt-2"}`}>
                              {lastOfGroup ? (
                                <Avatar
                                  name={m.is_me ? member?.name : active.name}
                                  img={m.is_me ? member?.img : active.img}
                                  primary={primary}
                                  size={30}
                                />
                              ) : (
                                <span className="w-[30px] shrink-0" />
                              )}
                              <div className={`flex max-w-[80%] flex-col ${m.is_me ? "items-end" : "items-start"}`}>
                                <div className={`relative flex items-center gap-1.5 ${m.is_me ? "flex-row" : "flex-row-reverse"}`}>
                                  {caretBtn}
                                  <div
                                    className={`min-w-0 px-4 py-2.5 text-[13.5px] leading-relaxed shadow-sm rounded-2xl ${
                                      lastOfGroup ? (m.is_me ? "rounded-br-md" : "rounded-bl-md") : ""
                                    } ${m.is_me ? "" : "bg-white text-slate-800 ring-1 ring-black/5"}`}
                                    style={m.is_me && !m.deleted ? myBubble : m.is_me ? { backgroundColor: "#f1f5f9", color: "#64748b" } : undefined}
                                  >
                                    {m.reply_to && (
                                      <button
                                        type="button"
                                        onClick={() => scrollToMessage(m.reply_to.id)}
                                        className="mb-1.5 block w-full overflow-hidden rounded-lg border-l-[3px] bg-black/5 px-2 py-1 text-left"
                                        style={{ borderColor: primary }}
                                      >
                                        <span className="block text-[11px] font-bold" style={{ color: primary }}>
                                          {m.reply_to.sender_id === myId ? "You" : active.name}
                                        </span>
                                        <span className="block truncate text-[11px] text-slate-500">
                                          {m.reply_to.deleted ? "Message deleted" : m.reply_to.preview}
                                        </span>
                                      </button>
                                    )}
                                    {m.deleted ? (
                                      <span className="text-[13px] italic text-slate-400">This message was deleted</span>
                                    ) : isImageUrl(m.body) ? (
                                      <a href={m.body} target="_blank" rel="noreferrer" className="block">
                                        <img src={m.body} alt="" className="max-h-52 w-auto max-w-full rounded-xl" loading="lazy" />
                                      </a>
                                    ) : (
                                      <span className="whitespace-pre-wrap break-words">{m.body}</span>
                                    )}
                                  </div>

                                  {/* Premium message menu: reaction bar + labelled actions */}
                                {reactFor === m.id && (
                                  <>
                                    <div className="fixed inset-0 z-[60]" onClick={() => setReactFor(null)} />
                                    <div className={`absolute z-[61] w-56 max-w-[calc(100vw-1.5rem)] rounded-2xl bg-white p-2 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.25)] ring-1 ring-black/5 ${menuDir === "up" ? "bottom-full mb-2" : "top-full mt-2"} ${m.is_me ? "right-0" : "left-0"}`}>
                                      {/* reaction bar */}
                                      <div className="mb-1.5 flex items-center justify-between gap-0.5 rounded-xl bg-slate-50 px-1.5 py-1.5">
                                        {QUICK_REACTIONS.map((e) => (
                                          <button
                                            key={e}
                                            type="button"
                                            onClick={() => toggleReaction(m, e)}
                                            className="flex h-8 w-8 items-center justify-center rounded-full text-xl leading-none transition hover:scale-125 hover:bg-white hover:shadow-sm"
                                          >
                                            {e}
                                          </button>
                                        ))}
                                      </div>

                                      {/* action icons */}
                                      <div className="flex items-center justify-around gap-1 px-0.5">
                                        <button type="button" title="Reply" aria-label="Reply" onClick={() => startReply(m)} className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100">
                                          <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17l-5-5 5-5M4 12h11a4 4 0 014 4v2" /></svg>
                                        </button>
                                        {!isImageUrl(m.body) && (
                                          <button type="button" title="Copy" aria-label="Copy" onClick={() => copyMsg(m)} className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100">
                                            <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><rect x="9" y="9" width="11" height="11" rx="2" /><path strokeLinecap="round" strokeLinejoin="round" d="M5 15V5a2 2 0 012-2h10" /></svg>
                                          </button>
                                        )}
                                        {m.is_me && !isImageUrl(m.body) && (
                                          <button type="button" title="Edit" aria-label="Edit" onClick={() => startEdit(m)} className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100">
                                            <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                                          </button>
                                        )}
                                        {m.is_me && (
                                          <button type="button" title="Delete" aria-label="Delete" onClick={() => { deleteMsg(m.id); setReactFor(null); }} className="flex h-9 w-9 items-center justify-center rounded-xl text-red-500 transition hover:bg-red-50">
                                            <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5h6v2m-7 0l1 12h6l1-12" /></svg>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                )}
                                </div>

                                {/* reactions */}
                                {m.reactions && m.reactions.length > 0 && (
                                  <div className={`mt-1 flex flex-wrap gap-1 ${m.is_me ? "justify-end" : "justify-start"}`}>
                                    {m.reactions.map((r) => (
                                      <button
                                        key={r.emoji}
                                        type="button"
                                        onClick={() => toggleReaction(m, r.emoji)}
                                        className={`flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] ${r.mine ? "border-transparent" : "border-slate-200 bg-white"}`}
                                        style={r.mine ? { backgroundColor: `${primary}1f` } : undefined}
                                      >
                                        <span>{r.emoji}</span>
                                        <span className="font-semibold text-slate-600">{r.count}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}

                                {/* timestamp + ticks — once per message group */}
                                {lastOfGroup && (
                                  <div className="mt-1 flex items-center gap-1 px-1 text-[10px] text-slate-400">
                                    <span>{shortTime(m.created_at)}</span>
                                    {m.edited && !m.deleted && <span>· edited</span>}
                                    {m.is_me && !m.deleted && <ReadTicks read={m.read} delivered={m.delivered} />}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Fragment>
                        );
                      });
                    })()}
                    {otherTyping && (
                      <div className="mt-4 flex items-end gap-2">
                        <Avatar name={active.name} img={active.img} primary={primary} size={30} />
                        <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-white px-3.5 py-2.5 shadow-sm ring-1 ring-black/5">
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: "0ms" }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: "150ms" }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {editing && (
                <div className="flex items-center justify-between border-t border-slate-100 bg-amber-50 px-4 py-1.5 text-xs text-amber-700">
                  <span>Editing message</span>
                  <button type="button" onClick={() => { setEditing(null); setText(""); }} className="font-semibold hover:underline">
                    Cancel
                  </button>
                </div>
              )}

              {replyTo && !editing && (
                <div className="flex items-center gap-2.5 border-t border-slate-100 bg-slate-50 px-4 py-2">
                  <span className="h-9 w-1 shrink-0 rounded-full" style={{ backgroundColor: primary }} />
                  <div className="min-w-0 flex-1">
                    <span className="block text-[11px] font-bold" style={{ color: primary }}>
                      Replying to {replyTo.is_me ? "You" : active.name}
                    </span>
                    <span className="block truncate text-xs text-slate-500">{replyTo.preview}</span>
                  </div>
                  <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply" className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              )}

              {isActiveBlocked && (
                <div className="border-t border-slate-100 bg-rose-50 px-4 py-3 text-center">
                  <p className="text-sm font-semibold text-rose-600">You blocked this member</p>
                  <p className="mt-0.5 text-xs text-rose-400">Unblock to send and receive messages.</p>
                  <button
                    type="button"
                    onClick={() => unblockMember(active.member_token)}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-xs font-bold text-rose-600 ring-1 ring-rose-200 transition hover:bg-rose-100"
                  >
                    <BanIcon className="h-3.5 w-3.5" /> Unblock
                  </button>
                </div>
              )}

              <div className={`border-t border-slate-100 bg-white px-3 py-3 sm:px-4 ${isActiveBlocked ? "hidden" : ""}`}>
                <div className="flex items-end gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 px-2 py-1.5 transition focus-within:border-slate-300 focus-within:bg-white">
                  <div className="flex items-center gap-1 pb-1.5 pl-1">
                    <EmojiPicker onPick={(e) => setText((t) => t + e)} dropUp />
                    {!editing && <GifPicker onPick={sendGif} dropUp />}
                    {!editing && (
                      <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} aria-label="Attach image" className="flex items-center justify-center text-slate-500 transition hover:text-slate-700 disabled:opacity-50">
                        {uploading ? (
                          <Spinner size={18} />
                        ) : (
                          <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L21 15M5 5h14a1 1 0 011 1v12a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" /><circle cx="9" cy="9" r="1.4" /></svg>
                        )}
                      </button>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) uploadImage(f);
                      }}
                    />
                  </div>
                  <textarea
                    rows={1}
                    value={text}
                    onChange={onType}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder={editing ? "Edit your message…" : "Write a message…"}
                    className="max-h-32 flex-1 resize-none border-0 bg-transparent px-1 py-1.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-0 focus:outline-none focus:ring-0"
                  />
                  <button
                    type="button"
                    onClick={send}
                    disabled={sending || !text.trim()}
                    aria-label={editing ? "Save" : "Send"}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white shadow-md transition active:scale-95 disabled:opacity-40 disabled:shadow-none"
                    style={sentBubble}
                  >
                    {editing ? (
                      <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.5 4.5l17 7.5-17 7.5L6 12zm0 0h6" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="hidden flex-1 flex-col items-center justify-center gap-3 bg-gradient-to-b from-slate-50 to-white text-center sm:flex">
              <span className="flex h-20 w-20 items-center justify-center rounded-full" style={{ backgroundColor: `${primary}10`, color: primary }}>
                <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.3">
                  <path strokeLinecap="round" strokeLinejoin="round" d={chatGlyph} />
                </svg>
              </span>
              <p className="text-sm font-semibold text-slate-600">Your messages</p>
              <p className="max-w-[220px] text-xs text-slate-400">Select a conversation or start a new one to begin chatting.</p>
            </div>
          )}
        </section>
      </div>
    </div>,
    document.body
  );
}
